import React, { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useRouter } from "next/router";
import { stringify } from "query-string";
import styled from "styled-components";
import MuiNativeSelect, {
  NativeSelectProps as MuiNativeSelectProps,
} from "@material-ui/core/NativeSelect";
import {
  compose,
  map,
  uniq,
  uniqBy,
  path,
  groupBy,
  filter,
  findIndex,
  flatten,
  prop,
} from "ramda";
import PageLayout from "lib/components/PageLayout";
import { useTheme } from "lib/contexts/themeContext";
import useIsResMobile from "lib/hooks/useIsResMobile";
import IllustClient from "lib/assets/img-client.svg";
import IllustLawyer from "lib/assets/img-lawyer.svg";
import IconFilter from "lib/assets/filter-black.svg";
import IconQMark from "lib/assets/q-mark.svg";
import IconClose from "lib/assets/close-modal.svg";
import IconStars from "lib/assets/stars.svg";
import IconStarsMobile from "lib/assets/stars-mobile.svg";
import RequestService from "domain/consultation-request/requestService";
import languageCodes from "lib/select-options/languages";
import usStates from "lib/select-options/usStates";
import {
  getIntlLocale,
  useLanguageName,
  useLocale,
  useTranslations,
} from "lib/i18n";
import { SelectOption } from "lib/components/Select";
import Switch from "lib/components/Switch";
import CardLawyer from "domain/consultation-request/CardLawyer";
import { usePracticeArea } from "domain/consultation-request/usePracticeArea";
import { useEvent } from "lib/contexts/eventContext";
import { CustomEventLabel } from "lib/enums";
import Head from "lib/components/Head";
import { SpinFixed } from "lib/components/Spin";
import { purify } from "lib/format";

export const getServerSideProps = async ({ params }) => {
  const { state } = params;
  const service = new RequestService();
  const listLawyer = await service.getListLawyer();
  const getUniqPracticeAreas = compose(
    uniqBy(prop("typeId")),
    flatten,
    map(prop("specialityTypes")),
  );
  const availablePracticeAreas = getUniqPracticeAreas(listLawyer);
  const uniqLanguages = uniq(flatten(map(prop("languages"), listLawyer)));
  const availableLanguages = filter(
    (lang) =>
      findIndex((uniqLanguage) => uniqLanguage === lang.value, uniqLanguages) >
      -1,
    languageCodes,
  );

  // CONSTRUCT LAWYER LIST BY STATE
  const uniqStates = uniq(map(path(["address", "state"]), listLawyer));
  const filteredStates = filter(
    (state) =>
      findIndex((uniqState) => uniqState === state.value, uniqStates) > -1,
    usStates,
  );
  const availableStates = map(
    (state) => ({
      ...state,
      key: state.value,
      label: state.value,
    }),
    filteredStates,
  );

  return {
    props: {
      state: state?.toUpperCase(),
      availableStates,
      availablePracticeAreas,
      availableLanguages,
      stateString:
        usStates.find((usState) => usState.value === state.toUpperCase())
          ?.description || null,
      listLawyer,
    },
  };
};

interface LawyersProps {
  state: string;
  stateString: string;
  availableStates: SelectOption[];
  availablePracticeAreas: {
    typeId: number;
    typeName: string;
    description: string;
  }[];
  availableLanguages: SelectOption[];
  listLawyer: ResLawyerListItem[];
}

const Lawyers: React.FC<LawyersProps> = ({
  state,
  stateString,
  availableStates,
  availablePracticeAreas,
  availableLanguages,
  listLawyer,
}) => {
  const t = useTranslations("lawyers");
  const languageName = useLanguageName();
  const locale = useLocale();
  const practiceArea = usePracticeArea();
  const { tag } = useEvent();
  const { query, push } = useRouter();
  const isResMobile = useIsResMobile();
  const [selectedTypeId, setTypeId] = useState("all");
  const [selectedLang, setLang] = useState("all");
  const [onlyLive, setOnlyLive] = useState(false);
  const [showPracticeAreaHelp, setShowPracticeAreaHelp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data } = useSWR<ResLawyerListItem[]>(
    "/lawyers/profiles",
    () => new RequestService().getListLawyer(),
    { fallbackData: listLawyer },
  );
  const listLawyerRuntime = useMemo(() => {
    if (state.toLowerCase() === "all") {
      return data;
    }
    const lawyersByState = groupBy(path<string>(["address", "state"]))(data);
    return lawyersByState[state.toUpperCase()] || [];
  }, [data, state]);

  /**
   * Mobile Filter related states
   */
  const [showFilterMobile, setShowFilterMobile] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const defaultFilterMobile = useMemo(
    () => ({
      typeId: "all",
      lang: "all",
      state: state ?? "all",
    }),
    [state],
  );
  const [filterMobile, setFilterMobile] = useState<{
    typeId: string;
    lang: string;
    state: string;
  }>(defaultFilterMobile);

  /**
   * We keep a copy in case filter view is dismissed
   */
  const [filterMobilePrev, setFilterMobilePrev] = useState<
    typeof defaultFilterMobile | null
  >(null);

  /**
   * Determine if current filter configuration has filter applied
   */
  useEffect(() => {
    if (
      filterMobile.lang === "all" &&
      filterMobile.state === "all" &&
      filterMobile.typeId === "all"
    ) {
      setIsFilterApplied(false);
    } else {
      setIsFilterApplied(true);
    }
  }, [filterMobile]);

  useEffect(() => {
    const { practice, onlyLive, lang } = query;

    if (lang) {
      setFilterMobile((filter) => ({ ...filter, lang: lang as string }));
      setLang(lang as string);
    } else {
      setLang("all");
    }
    if (practice) {
      setFilterMobile((filter) => ({
        ...filter,
        typeId: practice as string,
      }));
      setTypeId(practice as string);
    } else {
      setTypeId("all");
    }
    if (onlyLive) {
      setOnlyLive(true);
    } else {
      setOnlyLive(false);
    }
  }, [query]);

  const filteredLawyers = useMemo(() => {
    const filteredByPractice =
      selectedTypeId === "all"
        ? listLawyerRuntime
        : listLawyerRuntime.filter(
            (lawyer) =>
              lawyer.specialityTypes.findIndex(
                (specialityType) =>
                  String(specialityType.typeId) === selectedTypeId,
              ) > -1,
          );

    const filteredByLang =
      selectedLang === "all"
        ? filteredByPractice
        : filteredByPractice.filter(
            (lawyer) =>
              lawyer.languages.findIndex(
                (lang) => String(lang) === selectedLang,
              ) > -1,
          );

    const filteredByLiveStatus = !onlyLive
      ? filteredByLang
      : filteredByLang.filter((lawyer) => lawyer.isOnline);
    return filteredByLiveStatus;
  }, [selectedTypeId, listLawyerRuntime, onlyLive, selectedLang]);

  const promotedLawyers = useMemo(() => {
    const grouped = groupBy(
      (lawyer) =>
        lawyer.defaultPaymentFee.previousPromotionAttorneyFee
          ? "promoted"
          : "notPromoted",
      filteredLawyers,
    );
    return [
      ...(grouped.promoted ? grouped.promoted : []),
      ...(grouped.notPromoted ? grouped.notPromoted : []),
    ];
  }, [filteredLawyers]);

  const orderedLaywers = useMemo(() => {
    const grouped = groupBy(
      (lawyer) => (lawyer.isOnline ? "online" : "offline"),
      promotedLawyers,
    );
    return [
      ...(grouped.online ? grouped.online : []),
      ...(grouped.offline ? grouped.offline : []),
    ];
  }, [promotedLawyers]);

  const handleOnChangeState = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    tag?.(CustomEventLabel.FindALawyerFilterState, { value });

    if (value === "all") {
      push(`/lawyers/states/all`);
    } else {
      push(`/lawyers/states/${value.toLowerCase()}`);
    }
  };

  const handleOnChangeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    tag?.(CustomEventLabel.FindALawyerFilterLanguage, { value });

    if (value === "all") {
      push(
        `?${stringify({ ...query, lang: undefined })}`,
        `?${stringify({ ...query, lang: undefined })}`,
        { shallow: true },
      );
    } else {
      push(
        `?${stringify({ ...query, lang: value })}`,
        `?${stringify({ ...query, lang: value })}`,
        { shallow: true },
      );
    }
  };

  const handleOnChangePracticeArea = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { value } = e.target;

    tag?.(CustomEventLabel.FindALawyerFilterPracticeArea, { value });

    if (value === "all") {
      push(
        `?${stringify({ ...query, practice: undefined })}`,
        `?${stringify({ ...query, practice: undefined })}`,
        { shallow: true },
      );
    } else {
      push(
        `?${stringify({ ...query, practice: String(value) })}`,
        `?${stringify({ ...query, practice: String(value) })}`,
        { shallow: true },
      );
    }
  };

  const handleOnChangeOnlyLive = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    tag?.(CustomEventLabel.FindALawyerFilterLiveOnly, { value: checked });

    if (checked) {
      push(
        `?${stringify({ ...query, onlyLive: true })}`,
        `?${stringify({ ...query, onlyLive: true })}`,
        { shallow: true },
      );
    } else {
      push(
        `?${stringify({ ...query, onlyLive: undefined })}`,
        `?${stringify({ ...query, onlyLive: undefined })}`,
        { shallow: true },
      );
    }
  };

  const handleOnTogglePracticeAreaHelpMobile = () => {
    setShowPracticeAreaHelp((val) => !val);
  };

  const handleOnToggleFilterMobile = () => {
    setShowFilterMobile((val) => {
      if (!val) {
        setFilterMobilePrev(filterMobile);
        return !val;
      } else {
        if (filterMobilePrev) {
          // restore older config
          setFilterMobile(filterMobilePrev);
        }
        return !val;
      }
    });
  };

  const makeHandleOnChangeFilterMobile = useCallback(
    (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = e.target.value;

      setFilterMobile({
        ...filterMobile,
        [key]: val,
      });
    },
    [filterMobile],
  );

  const handleOnSetFilterMobile = useCallback(async () => {
    let navBuilder = `/lawyers/states/${filterMobile.state.toLowerCase()}`;
    if (filterMobile.typeId !== "all" || filterMobile.lang !== "all") {
      navBuilder += `?${stringify({
        practice:
          filterMobile.typeId === "all" ? undefined : filterMobile.typeId,
        lang: filterMobile.lang === "all" ? undefined : filterMobile.lang,
      })}`;
    }
    setIsLoading(true);
    setShowFilterMobile(false);
    await push(navBuilder, navBuilder);
    setIsLoading(false);
  }, [filterMobile.lang, filterMobile.state, filterMobile.typeId, push]);

  const handleOnClearFilterMobile = useCallback(async () => {
    setIsLoading(true);
    setShowFilterMobile(false);
    setFilterMobile({
      typeId: "all",
      lang: "all",
      state: "all",
    });
    await push(`/lawyers/states/all`);
    setIsLoading(false);
  }, [push]);

  /**
   * Ordered by the label the visitor actually reads. Sorting twenty options by
   * their English names would come out looking arbitrary in Spanish, and
   * `localeCompare` puts accented initials where a Spanish reader expects them.
   * Copying the array also stops the sort from mutating the prop in place.
   */
  const sortedPracticeAreas = useMemo(
    () =>
      [...availablePracticeAreas].sort((a, b) =>
        practiceArea
          .name(a)
          .localeCompare(practiceArea.name(b), getIntlLocale(locale)),
      ),
    [availablePracticeAreas, practiceArea, locale],
  );

  const practiceAreaHelpCore = useMemo(
    () =>
      sortedPracticeAreas.map((area) => (
        <React.Fragment key={area.typeId}>
          <h5 style={{ fontSize: 15 }}>{practiceArea.name(area)}</h5>
          <p
            css={`
              font-size: 13px;
              margin-bottom: 50px;
            `}
            dangerouslySetInnerHTML={{
              // The API separates these sentences with <br><br>, and the default
              // allow-list dropped them, running a seven-sentence entry together
              // with no space after the periods. <br> is the only tag the data
              // contains.
              __html: purify(practiceArea.description(area), ["a", "br"]),
            }}
          />
        </React.Fragment>
      )),
    [sortedPracticeAreas, practiceArea],
  );

  const metaUrl = `${process.env.NEXT_PUBLIC_HOMEPAGE_URL}/lawyers/${
    state ? `states/${state.toLowerCase()}` : ""
  }`;
  const metaDescription =
    "Our platform helps immigrants find the best lawyers for their case using our searching algorithm. Lawyers can provide consultation for their new clients with video or text chatting.";
  const metaTitle = `Immigration lawyers available for video consultation${
    stateString ? ` in ${stateString}` : ``
  } | Lawfully`;

  return (
    <PageLayout>
      <Head
        title={metaTitle}
        desc={metaDescription}
        url={metaUrl}
        imageUrl={"/share-image-general.jpg"}
      >
        <script type="application/ld+json">{`
          {
            "@context": "http://schema.org",
            "@type": "ItemList",
            "itemListElement": [${listLawyerRuntime
              .map((lawyer, idx) => {
                const formattedDescription = lawyer.aboutLawyer
                  ?.replace(/(\r\n|\n|\r)/gm, " ")
                  .substring(0, 200)
                  .concat("...");
                const consultationRate =
                  Math.round((lawyer.hourlyRate / 4) * 100) / 100; // 25.9999 -> 25.99
                const reviewRating =
                  Math.round(lawyer.averageStarRate * 100) / 100; // 3.33333 -> 3.33

                return `{
                  "@type": "ListItem",
                  "position": ${idx + 1},
                  "item": {
                    "@type": "Product",
                    "url": "${process.env.NEXT_PUBLIC_HOMEPAGE_URL}/lawyers/${
                  lawyer.urlSlug
                }/",
                    "name": "${lawyer.lawyerName}",
                    "description": "${formattedDescription}",
                    "aggregateRating": {
                      "@type": "AggregateRating",
                      "ratingValue": "${reviewRating}",
                      "reviewCount": "${lawyer.numOfReviews}"
                    },
                    "offers": {
                      "@type": "Offer",
                      "url": "${process.env.NEXT_PUBLIC_HOMEPAGE_URL}/lawyers/${
                  lawyer.urlSlug
                }/",
                      "priceCurrency": "USD",
                      "price": "${consultationRate}"
                    }
                  }
              }`;
              })
              .join(", ")}],
            "itemListOrder": "http://schema.org/ItemListOrderDescending",
            "name": "Video Consultation Lawyers${
              stateString ? ` in ${stateString}` : ``
            }"
          }
      `}</script>
      </Head>

      {showPracticeAreaHelp && (
        <PracticeAreaHelpMobile>
          <div
            css={`
              display: flex;
              align-items: center;
              padding-left: 20px;
              height: 70px;
              font-weight: normal;
              font-size: 20px;
            `}
          >
            <div onClick={handleOnTogglePracticeAreaHelpMobile}>
              <IconClose
                css={`
                  cursor: pointer;
                  fill: black;
                `}
              />
            </div>
          </div>
          <PracticeAreaHelpContentMobile>
            {practiceAreaHelpCore}
          </PracticeAreaHelpContentMobile>
        </PracticeAreaHelpMobile>
      )}
      {showFilterMobile && (
        <FilterMobile>
          <div
            css={`
              display: flex;
              align-items: center;
              padding: 15px 20px;
              height: 70px;
              font-weight: normal;
              font-size: 20px;
              justify-content: space-between;
            `}
          >
            <div
              onClick={handleOnToggleFilterMobile}
              css={`
                width: 50px;
              `}
            >
              <IconClose
                css={`
                  cursor: pointer;
                `}
              />
            </div>
            <div
              css={`
                font-weight: bold;
                font-size: 16px;
                line-height: 24px;
              `}
            >
              Filter
            </div>
            <button
              css={`
                border: none;
                font-weight: 500;
                font-size: 14px;
                line-height: 22px;
                color: #195dee;
                background: transparent;
              `}
              onClick={handleOnClearFilterMobile}
            >
              Clear
            </button>
          </div>
          <FilterMobileContent>
            <div
              css={`
                display: flex;
                flex-direction: column;
              `}
            >
              <div
                css={`
                  flex-basis: 1;
                  flex-grow: 1;
                  flex-shrink: 0;
                  margin: 0 10px 10px 0;
                `}
              >
                <SimpleSelect
                  name="state"
                  value={filterMobile.state ?? "all"}
                  onChange={makeHandleOnChangeFilterMobile("state")}
                >
                  <option value="all">{t("filters.allStates")}</option>
                  {availableStates.map((state) => (
                    <option key={state.key} value={state.value}>
                      {/* US state names stay English in every locale: they are
                          official place names that appear on USCIS forms and
                          addresses, and a translated label would not match what
                          the applicant is reading off their paperwork. */}
                      {state.description}
                    </option>
                  ))}
                </SimpleSelect>
              </div>
              <div
                css={`
                  flex-basis: 1;
                  flex-grow: 1;
                  flex-shrink: 0;
                  margin: 0 10px 10px 0;
                `}
              >
                <SimpleSelect
                  name="languages"
                  value={filterMobile.lang}
                  onChange={makeHandleOnChangeFilterMobile("lang")}
                >
                  <option value="all">{t("filters.allLanguages")}</option>
                  {availableLanguages.map((lang) => (
                    <option key={lang.key} value={lang.value}>
                      {languageName(String(lang.value), lang.label)}
                    </option>
                  ))}
                </SimpleSelect>
              </div>
              <div
                css={`
                  order: -1;
                  flex-basis: 100%;
                  flex-grow: 1;
                  margin-bottom: 10px;
                  display: flex;
                  align-items: center;
                `}
              >
                <div
                  css={`
                    margin-right: 10px;
                    max-width: 100%;
                    flex-basis: auto;
                    flex-grow: 1;
                  `}
                >
                  <SimpleSelect
                    name="practice-areas"
                    value={filterMobile.typeId}
                    onChange={makeHandleOnChangeFilterMobile("typeId")}
                  >
                    <option value="all">{t("filters.allPracticeAreas")}</option>
                    {sortedPracticeAreas.map((type) => (
                      <option key={type.typeId} value={type.typeId}>
                        {practiceArea.name(type)}
                      </option>
                    ))}
                  </SimpleSelect>
                </div>
                <PracticeAreaHelpWrapper
                  css={`
                    display: none;
                  `}
                >
                  <IconQMark
                    css={`
                      cursor: pointer;
                      &:hover ~ aside {
                        opacity: 1;
                        visibility: visible;
                        transition: opacity 0.2s linear, visibility 0s linear;
                      }
                    `}
                  />
                  <PracticeAreaHelp>
                    <PracticeAreaHelpContent>
                      {practiceAreaHelpCore}
                    </PracticeAreaHelpContent>
                  </PracticeAreaHelp>
                </PracticeAreaHelpWrapper>
                <div
                  onClick={handleOnTogglePracticeAreaHelpMobile}
                  css={`
                    display: flex;
                  `}
                >
                  <IconQMark
                    css={`
                      cursor: pointer;
                      &:hover ~ aside {
                        opacity: 1;
                        visibility: visible;
                        transition: opacity 0.2s linear, visibility 0s linear;
                      }
                    `}
                  />
                </div>
              </div>
            </div>

            <div
              css={`
                width: 100%;
                padding: 20px;
              `}
            >
              <button
                css={`
                  background: #195dee;
                  border-radius: 30px;
                  font-weight: 500;
                  font-size: 16px;
                  line-height: 24px;
                  color: white;
                  padding: 16px 20px;
                  width: 100%;
                `}
                onClick={handleOnSetFilterMobile}
              >
                Show results
              </button>
            </div>
          </FilterMobileContent>
        </FilterMobile>
      )}

      <Hero state={stateString} />

      <div
        css={`
          max-width: 960px;
          margin: 50px auto;
          padding: 0 ${isResMobile ? 20 : 50}px;
        `}
      >
        <div
          css={`
            margin-bottom: 50px;
            display: flex;
            align-items: center;
            justify-content: ${isResMobile ? "space-between" : `flex-start`};
            flex-wrap: wrap;
          `}
        >
          <ButtonFilterMobile
            onClick={handleOnToggleFilterMobile}
            css={`
              display: ${isResMobile ? `flex` : `none`};
              position: relative;
            `}
          >
            <IconFilter
              css={`
                width: 18px;
                height: 18px;
                margin-right: 7px;
              `}
              viewBox="0 0 24 24"
            />
            {isFilterApplied && (
              <DotBlue
                css={`
                  position: absolute;
                  left: 32px;
                  top: 10px;
                `}
              />
            )}
            <div
              css={`
                font-size: 14px;
              `}
            >
              Filter
            </div>
          </ButtonFilterMobile>
          <div
            css={`
              display: ${isResMobile ? `none` : `block`};
              flex-basis: ${isResMobile ? 1 : `200px`};
              flex-grow: 1;
              flex-shrink: 0;
              margin: ${isResMobile ? `0 10px 10px 0` : `0 10px 10px 0`};
            `}
          >
            <SimpleSelect
              name="state"
              value={state ?? "all"}
              onChange={handleOnChangeState}
            >
              <option value="all">{t("filters.allStates")}</option>
              {availableStates.map((state) => (
                <option key={state.key} value={state.value}>
                  {/* English in every locale -- see the mobile filter above. */}
                  {state.description}
                </option>
              ))}
            </SimpleSelect>
          </div>
          <div
            css={`
              display: ${isResMobile ? `none` : `block`};
              flex-basis: ${isResMobile ? 1 : `200px`};
              flex-grow: 1;
              flex-shrink: 0;
              margin: ${isResMobile ? `0 10px 10px 0` : `0 10px 10px 0`};
            `}
          >
            <SimpleSelect
              css={`
                font-size: 14px;
              `}
              name="languages"
              value={selectedLang}
              onChange={handleOnChangeLanguage}
            >
              <option value="all">{t("filters.allLanguages")}</option>
              {availableLanguages.map((lang) => (
                <option key={lang.key} value={lang.value}>
                  {languageName(String(lang.value), lang.label)}
                </option>
              ))}
            </SimpleSelect>
          </div>
          <div
            css={`
              order: ${isResMobile ? -1 : 0};
              flex-basis: ${isResMobile ? `100%` : `300px`};
              flex-grow: 1;
              margin-bottom: 10px;
              display: ${isResMobile ? `none` : `flex`};
              align-items: center;
            `}
          >
            <div
              css={`
                padding-right: 10px;
                max-width: ${isResMobile ? `100%` : `200px`};
                flex-basis: ${isResMobile ? `auto` : `200px`};
                flex-grow: 1;
              `}
            >
              <SimpleSelect
                name="practice-areas"
                value={selectedTypeId}
                onChange={handleOnChangePracticeArea}
              >
                <option value="all">{t("filters.allPracticeAreas")}</option>
                {sortedPracticeAreas.map((type) => (
                  <option key={type.typeId} value={type.typeId}>
                    {practiceArea.name(type)}
                  </option>
                ))}
              </SimpleSelect>
            </div>
            <PracticeAreaHelpWrapper
              css={`
                display: ${isResMobile ? `none` : `flex`};
              `}
            >
              <IconQMark
                css={`
                  cursor: pointer;
                  &:hover ~ aside {
                    opacity: 1;
                    visibility: visible;
                    transition: opacity 0.2s linear, visibility 0s linear;
                  }
                `}
              />
              <PracticeAreaHelp>
                <PracticeAreaHelpContent>
                  {practiceAreaHelpCore}
                </PracticeAreaHelpContent>
              </PracticeAreaHelp>
            </PracticeAreaHelpWrapper>
            <div
              onClick={handleOnTogglePracticeAreaHelpMobile}
              css={`
                display: ${isResMobile ? `flex` : `none`};
              `}
            >
              <IconQMark
                css={`
                  cursor: pointer;
                  &:hover ~ aside {
                    opacity: 1;
                    visibility: visible;
                    transition: opacity 0.2s linear, visibility 0s linear;
                  }
                `}
              />
            </div>
          </div>
          <Switch
            css={`
              margin-bottom: 10px;
            `}
            label={t("filters.liveOnly")}
            onChange={handleOnChangeOnlyLive}
            checked={onlyLive}
          />
        </div>
      </div>

      <div
        css={`
          margin: 0 auto 100px;
          padding: 0 ${isResMobile ? 20 : 50}px;
          max-width: 960px;
        `}
      >
        {orderedLaywers.map((lawyer) => (
          <CardLawyer
            key={lawyer.lawyerId}
            lawyer={lawyer}
            availableLanguages={availableLanguages}
          />
        ))}
      </div>
      {isLoading && <SpinFixed />}
    </PageLayout>
  );
};

export default Lawyers;

const Hero = ({ state }: { state: string | null }) => {
  const t = useTranslations("lawyers");
  const theme = useTheme();
  const isResMobile = useIsResMobile();

  return (
    <div
      css={`
        overflow: hidden;
        position: relative;
        background-color: ${theme.palette.primary.main};
        color: white;
      `}
    >
      {!isResMobile && (
        <div
          css={`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: ${isResMobile ? 440 : 1340}px;
          `}
        >
          <IllustClient
            css={`
              position: absolute;
              left: 0;
              bottom: 0;
              width: ${isResMobile ? 200 : 360}px;
              height: ${isResMobile ? 200 : 360}px;
            `}
            viewBox="0 0 360 360"
          />
          <IllustLawyer
            css={`
              position: absolute;
              right: 0;
              bottom: 0;
              width: ${isResMobile ? 200 : 360}px;
              height: ${isResMobile ? 200 : 360}px;
            `}
            viewBox="0 0 360 360"
          />
        </div>
      )}
      <div
        css={`
          margin: 0 auto 40px;
          padding-top: ${isResMobile ? 40 : 90}px;
          padding-bottom: ${isResMobile ? 0 : 120}px;
          padding-left: ${isResMobile ? 20 : 0}px;
          padding-right: ${isResMobile ? 20 : 0}px;
          max-width: 1440px;
          text-align: center;
        `}
      >
        <div
          css={`
            margin: 0 auto ${isResMobile ? 0 : 40}px;
            max-width: ${isResMobile ? 650 : 860}px;
            line-height: ${isResMobile ? 32 : 60}px;
            font-size: ${isResMobile ? 26 : 50}px;
            font-weight: bold;
          `}
        >
          {state ? t("hero.titleInState", { state }) : t("hero.title")}
        </div>

        {!isResMobile && (
          <>
            <div
              css={`
                margin: 0 auto ${isResMobile ? 0 : 20}px;
                padding: ${isResMobile ? 20 : 0}px 0;
                max-width: 660px;
                font-size: ${isResMobile ? 14 : 20}px;
                line-height: ${isResMobile ? 20 : 28}px;
              `}
            >
              {t("hero.subtitle")}
              <br />
              {t("hero.description")}
            </div>

            <div
              css={`
                margin-bottom: ${isResMobile ? 50 : 60}px;
                font-size: ${isResMobile ? 16 : 22}px;
                line-height: ${isResMobile ? 28 : 20}px;
                font-weight: bold;
              `}
            >
              {t("hero.cta")}
            </div>
          </>
        )}

        {!isResMobile && (
          <HeaderBottom>
            <HeaderBottomItem label={t("stats.averageFee")}>
              $55
            </HeaderBottomItem>
            <HeaderBottomItem label={t("stats.averageDuration")}>
              19min
            </HeaderBottomItem>
            <HeaderBottomItem label={t("stats.averageRating")}>
              4.8
              <div
                aria-hidden
                css={`
                  margin-left: ${isResMobile ? 3 : 5}px;
                  display: flex;
                `}
              >
                {isResMobile ? <IconStarsMobile /> : <IconStars />}
              </div>
            </HeaderBottomItem>
          </HeaderBottom>
        )}
      </div>
    </div>
  );
};

const HeaderBottom = styled.div`
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  > *:not(:last-child) {
    border-right: 1px solid #5e8ef3;
  }
`;

const HeaderBottomItem: React.FC<{ label: string }> = ({ label, children }) => {
  const isResMobile = useIsResMobile();

  return (
    <div
      css={`
        width: ${isResMobile ? 120 : 190}px;
        line-height: ${isResMobile ? 16 : 18}px;
        font-size: ${isResMobile ? 12 : 14}px;
      `}
    >
      <div
        css={`
          margin-bottom: 6px;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: ${isResMobile ? 22 : 32}px;
          line-height: ${isResMobile ? 30 : 40}px;
        `}
      >
        {children}
      </div>
      {label}
    </div>
  );
};

const PracticeAreaHelpWrapper = styled.div`
  position: relative;
`;

const PracticeAreaHelp = styled.aside`
  position: absolute;
  visibility: hidden;
  opacity: 0;
  top: 0;
  left: -180px;
  z-index: 2;
  transition: opacity 0.2s linear, visibility 0s linear 0.2s;
  padding: 45px 0;
  cursor: pointer;

  &:hover {
    visibility: visible;
    opacity: 1;
  }

  &:before {
    content: "";
    width: 20px;
    height: 20px;
    position: absolute;
    transform: rotate(45deg);
    top: 40px;
    left: 182px;
    background: #f3f4f8;
    z-index: -1;
  }
`;

const PracticeAreaHelpContent = styled.div`
  width: 400px;
  height: 600px;
  background: #f3f4f8;
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 20px 40px;
  overflow: auto;
`;

const PracticeAreaHelpMobile = styled.aside`
  position: fixed;
  display: flex;
  flex-direction: column;
  background: #f3f4f8;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  visibility: visible;
  opacity: 1;
  z-index: 999999;
`;

const PracticeAreaHelpContentMobile = styled(PracticeAreaHelpContent)`
  width: 100%;
  height: 100%;
  border-radius: 0;
`;

const FilterMobile = styled(PracticeAreaHelpMobile)`
  background: white;
  z-index: 99999;
`;

const FilterMobileContent = styled(PracticeAreaHelpContentMobile)`
  width: 100%;
  height: 100%;
  border-radius: 0;
  background-color: white;
  padding: 15px;
  border-top: 1px solid #eeeeee;
  display: flex;
  flex-direction: column;
`;

const ButtonFilterMobile = styled.button`
  border: 1px solid #eeeeee;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 13px 16px;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 120px;
  margin-bottom: 10px;
`;

const DotBlue = styled.div`
  background: #195dee;
  width: 4px;
  height: 4px;
  border-radius: 50%;
`;

const SimpleSelect = (props: MuiNativeSelectProps) => {
  return (
    <MuiNativeSelect
      css={`
        &&& {
          padding: 8px 11px;
          border: 1px solid ${({ theme }) => theme.palette.grey[100]};
          border-radius: 10px;
          width: 100%;
          &:hover,
          &.Mui-focused {
            border-color: #195dee;
          }
          &&:before {
            border: none;
          }
          &&:after {
            border: none;
          }
          .MuiNativeSelect-select:focus {
            background: none;
          }
        }
      `}
      {...props}
      variant="filled"
    />
  );
};
